// SEO Analytics Service - Track search engine performance metrics
import { supabase } from './supabaseClient';

// Track page performance metrics (Core Web Vitals)
export const trackWebVitals = async (metric) => {
  try {
    const { name, value, id, delta } = metric;

    await supabase.from('web_vitals').insert({
      metric_name: name,
      metric_value: value,
      metric_id: id,
      delta: delta,
      page_url: window.location.pathname,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking web vitals:', error);
  }
};

// Track search queries that led to your site (from URL params)
export const trackSearchReferrer = async () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = document.referrer;

    // Common search engine patterns
    const searchEngines = {
      'google': /google\./i,
      'bing': /bing\./i,
      'yahoo': /yahoo\./i,
      'duckduckgo': /duckduckgo\./i,
      'baidu': /baidu\./i
    };

    let searchEngine = 'direct';
    let searchQuery = urlParams.get('q') || urlParams.get('query') || urlParams.get('utm_term') || '';

    for (const [engine, pattern] of Object.entries(searchEngines)) {
      if (pattern.test(referrer)) {
        searchEngine = engine;
        break;
      }
    }

    // Track UTM parameters
    const utmParams = {
      source: urlParams.get('utm_source'),
      medium: urlParams.get('utm_medium'),
      campaign: urlParams.get('utm_campaign'),
      content: urlParams.get('utm_content'),
      term: urlParams.get('utm_term')
    };

    if (searchEngine !== 'direct' || utmParams.source) {
      await supabase.from('search_referrers').insert({
        search_engine: searchEngine,
        search_query: searchQuery,
        referrer_url: referrer,
        landing_page: window.location.pathname,
        utm_source: utmParams.source,
        utm_medium: utmParams.medium,
        utm_campaign: utmParams.campaign,
        utm_content: utmParams.content,
        utm_term: utmParams.term,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Error tracking search referrer:', error);
  }
};

// Get SEO overview stats
export const getSEOOverview = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    // Get page views by page
    const { data: pageViews } = await supabase
      .from('page_views')
      .select('page_url')
      .gte('timestamp', startDate.toISOString());

    // Get search referrers
    const { data: referrers } = await supabase
      .from('search_referrers')
      .select('*')
      .gte('timestamp', startDate.toISOString());

    // Get web vitals
    const { data: vitals } = await supabase
      .from('web_vitals')
      .select('*')
      .gte('timestamp', startDate.toISOString());

    // Calculate page popularity
    const pageCounts = {};
    (pageViews || []).forEach(pv => {
      pageCounts[pv.page_url] = (pageCounts[pv.page_url] || 0) + 1;
    });

    const topPages = Object.entries(pageCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([url, count]) => ({ url, views: count }));

    // Calculate search engine breakdown
    const searchEngineCounts = {};
    (referrers || []).forEach(ref => {
      searchEngineCounts[ref.search_engine] = (searchEngineCounts[ref.search_engine] || 0) + 1;
    });

    // Calculate average web vitals
    const vitalAverages = {};
    const vitalCounts = {};
    (vitals || []).forEach(v => {
      if (!vitalAverages[v.metric_name]) {
        vitalAverages[v.metric_name] = 0;
        vitalCounts[v.metric_name] = 0;
      }
      vitalAverages[v.metric_name] += v.metric_value;
      vitalCounts[v.metric_name]++;
    });

    Object.keys(vitalAverages).forEach(key => {
      vitalAverages[key] = Math.round(vitalAverages[key] / vitalCounts[key]);
    });

    return {
      topPages,
      searchEngines: searchEngineCounts,
      webVitals: vitalAverages,
      totalOrganicVisits: referrers?.length || 0,
      totalPageViews: pageViews?.length || 0
    };
  } catch (error) {
    console.error('Error fetching SEO overview:', error);
    return {
      topPages: [],
      searchEngines: {},
      webVitals: {},
      totalOrganicVisits: 0,
      totalPageViews: 0
    };
  }
};

// Get search queries
export const getSearchQueries = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data } = await supabase
      .from('search_referrers')
      .select('search_query, search_engine, timestamp')
      .gte('timestamp', startDate.toISOString())
      .not('search_query', 'eq', '')
      .order('timestamp', { ascending: false })
      .limit(50);

    return data || [];
  } catch (error) {
    console.error('Error fetching search queries:', error);
    return [];
  }
};

// Get UTM campaign performance
export const getCampaignPerformance = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data } = await supabase
      .from('search_referrers')
      .select('utm_source, utm_medium, utm_campaign')
      .gte('timestamp', startDate.toISOString())
      .not('utm_source', 'is', null);

    // Group by campaign
    const campaigns = {};
    (data || []).forEach(row => {
      const key = `${row.utm_source || 'unknown'}/${row.utm_medium || 'unknown'}/${row.utm_campaign || 'unknown'}`;
      campaigns[key] = (campaigns[key] || 0) + 1;
    });

    return Object.entries(campaigns)
      .sort((a, b) => b[1] - a[1])
      .map(([campaign, visits]) => {
        const [source, medium, name] = campaign.split('/');
        return { source, medium, campaign: name, visits };
      });
  } catch (error) {
    console.error('Error fetching campaign performance:', error);
    return [];
  }
};

// Get Core Web Vitals history
export const getWebVitalsHistory = async (days = 7) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data } = await supabase
      .from('web_vitals')
      .select('*')
      .gte('timestamp', startDate.toISOString())
      .order('timestamp', { ascending: true });

    // Group by day and metric
    const history = {};
    (data || []).forEach(v => {
      const day = v.timestamp.split('T')[0];
      if (!history[day]) history[day] = {};
      if (!history[day][v.metric_name]) {
        history[day][v.metric_name] = { sum: 0, count: 0 };
      }
      history[day][v.metric_name].sum += v.metric_value;
      history[day][v.metric_name].count++;
    });

    return Object.entries(history).map(([date, metrics]) => {
      const result = { date };
      Object.entries(metrics).forEach(([name, { sum, count }]) => {
        result[name] = Math.round(sum / count);
      });
      return result;
    });
  } catch (error) {
    console.error('Error fetching web vitals history:', error);
    return [];
  }
};
